import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import colors from '../colors';
import useMap from './useMap';
import useMapStyle from './useMapStyle';
import { latLngToLngLat } from './utils';

export const defaultStyle = {
  lineColor: '#F7455D',
  lineWidth: 3,
  outlineColor: colors.WHITE,
  outlineWidth: 5,
  opacity: 1
};

function Polyline({ id, latLng, style: customStyle, beforeId }) {
  const map = useMap();
  const mapStyle = useMapStyle();
  const isMountedRef = useRef(true);

  const style = {
    ...defaultStyle,
    ...customStyle
  };
  const sourceName = `${id}-source`;

  const layer = {
    id,
    type: 'line',
    source: sourceName,
    layout: {
      'line-cap': 'round',
      'line-join': 'round'
    },
    paint: {
      'line-width': style.lineWidth,
      'line-color': style.lineColor,
      'line-color-transition': {
        duration: 0
      },
      'line-opacity': style.opacity
    }
  };

  const layerOutline = {
    ...layer,
    id: `${id}-outline`,
    source: sourceName,
    paint: {
      'line-width': style.outlineWidth,
      'line-color': style.outlineColor,
      'line-color-transition': {
        duration: 0
      },
      'line-opacity': style.opacity
    }
  };

  function addLayer() {
    if (!map.getSource(sourceName)) {
      map.addSource(sourceName, {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      });

      map.addLayer(layerOutline).addLayer(layer);
    }
  }

  function setData() {
    if (map.getSource(sourceName)) {
      map.getSource(sourceName).setData({
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: latLngToLngLat(latLng)
            },
            properties: {
              id
            }
          }
        ]
      });

      if (beforeId) {
        map.moveLayer(layerOutline.id, beforeId).moveLayer(layer.id, beforeId);
      }
    }
  }

  useEffect(() => {
    if (!isMountedRef.current && beforeId && map.getLayer(layer.id)) {
      map.moveLayer(layerOutline.id, beforeId).moveLayer(layer.id, beforeId);
    }
  }, [beforeId]);

  useEffect(() => {
    if (!isMountedRef.current) {
      addLayer();
      setData();
    }
  }, [mapStyle]);

  useEffect(() => {
    if (!isMountedRef.current) {
      setData();
    }
  }, [latLng]);

  useEffect(() => {
    if (!isMountedRef.current) {
      map.setPaintProperty(id, 'line-color', style.lineColor);
      map.setPaintProperty(id, 'line-width', style.lineWidth);
      map.setPaintProperty(id, 'line-opacity', style.opacity);
      map.setPaintProperty(`${id}-outline`, 'line-color', style.outlineColor);
      map.setPaintProperty(`${id}-outline`, 'line-width', style.outlineWidth);
      map.setPaintProperty(`${id}-outline`, 'line-opacity', style.opacity);
    }
  }, [
    style.lineColor,
    style.lineWidth,
    style.opacity,
    style.outlineColor,
    style.outlineWidth
  ]);

  useEffect(() => {
    addLayer();
    setData();

    isMountedRef.current = false;

    return function cleanup() {
      if (map && map.style && map.getLayer(layer.id)) {
        map.removeLayer(layer.id);
        map.removeLayer(layerOutline.id);
        map.removeSource(sourceName);
      }
    };
  }, []);

  return null;
}

Polyline.propTypes = {
  style: PropTypes.shape({
    lineColor: PropTypes.string,
    lineWidth: PropTypes.number,
    outlineColor: PropTypes.string,
    outlineWidth: PropTypes.number,
    opacity: PropTypes.number
  }),
  id: PropTypes.string.isRequired,
  latLng: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)).isRequired,
  beforeId: PropTypes.string
};

Polyline.defaultProps = {
  style: {},
  beforeId: null
};

export default Polyline;
